import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    detectCues: true,        // run CUE-DETR cue detection
    sensitivity: 0.82,       // CUE-DETR detection threshold (0-1)
    minDistanceBars: 16,     // minimum spacing between detected cues
    // How detected cues are saved:
    //   'hot'    -> top-`maxHotCues` (by confidence) go on the pads; any extra
    //              become memory cues so nothing is lost. (performance workflow)
    //   'memory' -> all detected cues become memory markers, pads left empty.
    //              (prep / CDJ-USB roadmap workflow; Rekordbox only — Serato/
    //              Traktor/VirtualDJ have no memory cues, so they always get hot)
    cueMode: 'hot',
    maxHotCues: 8,           // cap on hot cues (only used in 'hot' mode)
    // Snap detected cues onto the nearest beatgrid beat (window 45% of a
    // beat) — fixes CUE-DETR's tens-of-ms placement error; loops anchored to
    // cues land on the beat with them.
    quantizeCuesToGrid: true,
    // Downbeat vote (which of the 4 beats is "the 1"). 'merged' = the default:
    // Beat This! model downbeats + first-kick/backbeat + cue/boundary votes +
    // kick flux (scripts/beatgrid.py merged_downbeat_phase); 'kick' = the
    // previous kick-flux vote; 'structural' = the first-kick/backbeat vote
    // alone. When `downbeatCompare` is on (dev), Generate returns the merged
    // AND the previous placement from one analysis and the editor shows a
    // "Grid: new / previous" switch.
    downbeatMode: 'merged',
    downbeatCompare: process.env.NODE_ENV !== 'production',
    // Loop detection (runs in the same pass as cues when enabled).
    detectLoops: true,         // include loops when Generating
    loopBars: [4, 8, 16, 32],  // candidate loop lengths in bars
    maxLoops: 6,             // cap on auto-detected loops per track
    loopSensitivity: 0.5,    // higher = fewer, only the cleanest loops
    loopFocus: 'balanced',   // 'grooves' | 'balanced' | 'drops' — placement bias
    snapToBeat: true,        // snap loop edges to the beat/bar grid
    // Where loops land in Rekordbox (its 8 pads are ONE bank shared by cues
    // and loops — Serato has separate banks, so this is Rekordbox-only):
    //   'spare'    -> loops take pads left over after cues; extras -> memory loops
    //   'memory'   -> loops never take pads; all become memory loops
    //   'priority' -> loops claim pads first, cues fill the rest
    loopMode: 'spare',
    // Auto-arm the LAST loop as Rekordbox's Active Loop (CDJs engage it
    // automatically when playback reaches it; one per track). A manual
    // per-track choice in the editor always wins over this.
    autoActiveLoop: false,
    // Write cues into a playable ' [cues]' copy and leave the original
    // file untouched (same convention as Fix-Tracks' ' [fixed]' output).
    keepOriginalsUntouched: false,
    writeFileTags: true,     // write Serato GEOB tags into the audio files
    // Which DJ-software libraries to write cues into.
    targetRekordbox: true,   // Rekordbox on by default
    targetSerato: false,
    targetTraktor: false,
    targetVirtualdj: false,
};

const cuePointsSettingsSlice = createSlice({
    name: 'cuePointsSettings',
    initialState,
    reducers: {
        setDetectCues(state, action) { state.detectCues = action.payload; },
        setSensitivity(state, action) { state.sensitivity = action.payload; },
        setMinDistanceBars(state, action) { state.minDistanceBars = action.payload; },
        setMaxHotCues(state, action) { state.maxHotCues = action.payload; },
        setCueMode(state, action) { state.cueMode = action.payload === 'memory' ? 'memory' : 'hot'; },
        setQuantizeCuesToGrid(state, action) { state.quantizeCuesToGrid = action.payload; },
        setDownbeatMode(state, action) { state.downbeatMode = ['merged', 'kick', 'structural'].includes(action.payload) ? action.payload : 'merged'; },
        setDownbeatCompare(state, action) { state.downbeatCompare = !!action.payload; },
        setDetectLoops(state, action) { state.detectLoops = action.payload; },
        setLoopBars(state, action) { state.loopBars = action.payload; },
        setMaxLoops(state, action) { state.maxLoops = action.payload; },
        setLoopSensitivity(state, action) { state.loopSensitivity = action.payload; },
        setLoopFocus(state, action) { state.loopFocus = action.payload; },
        setSnapToBeat(state, action) { state.snapToBeat = action.payload; },
        setLoopMode(state, action) {
            state.loopMode = ['spare', 'memory', 'priority'].includes(action.payload) ? action.payload : 'spare';
        },
        setAutoActiveLoop(state, action) { state.autoActiveLoop = !!action.payload; },
        setKeepOriginalsUntouched(state, action) { state.keepOriginalsUntouched = action.payload; },
        setWriteFileTags(state, action) { state.writeFileTags = action.payload; },
        setTargetRekordbox(state, action) { state.targetRekordbox = action.payload; },
        setTargetSerato(state, action) { state.targetSerato = action.payload; },
        setTargetTraktor(state, action) { state.targetTraktor = action.payload; },
        setTargetVirtualdj(state, action) { state.targetVirtualdj = action.payload; },
        updateCuePointsSettings(state, action) {
            return { ...state, ...action.payload };
        },
    },
});

export const {
    setDownbeatMode,
    setDownbeatCompare,
    setDetectCues,
    setSensitivity,
    setMinDistanceBars,
    setMaxHotCues,
    setCueMode,
    setQuantizeCuesToGrid,
    setDetectLoops,
    setLoopBars,
    setMaxLoops,
    setLoopSensitivity,
    setLoopFocus,
    setSnapToBeat,
    setLoopMode,
    setAutoActiveLoop,
    setKeepOriginalsUntouched,
    setWriteFileTags,
    setTargetRekordbox,
    setTargetSerato,
    setTargetTraktor,
    setTargetVirtualdj,
    updateCuePointsSettings,
} = cuePointsSettingsSlice.actions;

// Resolve the list of target platform ids from the settings slice.
export const selectCueTargets = (state) => {
    const s = state.cuePointsSettings;
    const targets = [];
    if (s.targetRekordbox) targets.push('rekordbox');
    if (s.targetSerato) targets.push('serato');
    if (s.targetTraktor) targets.push('traktor');
    if (s.targetVirtualdj) targets.push('virtualdj');
    return targets;
};

export default cuePointsSettingsSlice.reducer;
